import { DESCRIPTOR_CATALOGUE, descriptorResolved, descriptorsForTemplate, itemDescriptorAnswer, propertyFactResolved, propertyFactValue, type DescriptorKey } from './descriptors';
import type { V2Area, V2ChecklistItem, V2Inspection } from './inspection';

// REPORT READINESS — the SECOND gate of a New York final client report (NY F1 Round 2, Owner
// correction OC-2; master §15.6; plan §11.2).
//
// The first gate is ELIGIBILITY (src/domain/inspectionContext.ts, Round 1): may this inspection
// continue the formal New York workflow at all? This module never restates it. It answers the next,
// independent question about an otherwise eligible inspection: is it READY for the final client
// report? An unresolved mandatory descriptor is a REPORT_READINESS_BLOCK.
//
// Resolved, by the Authority for each descriptor and never by the renderer:
//   - a listed lawful choice, or a non-empty text (descriptors.ts);
//   - a chosen Unknown ONLY where the Authority admits it as a printed value (197-5.8(d));
//   - a row that was NOT OBSERVED for a recorded limitation reason (Unable + reason code), or that is
//     not present (N/A · not installed): nothing is describable, the limitation / not-present entry
//     carries it;
//   - null (never asked) is never resolved and is never turned into Unknown.
// A row still without any result is the closeout's business (uninspected) and is not double-counted.
// Area completion is NEVER blocked by a descriptor (areaCompletionBlockers is untouched).
export type ReadinessBlockerKind = 'propertyFact' | 'descriptor';

export interface ReadinessBlocker {
  kind: ReadinessBlockerKind;
  key: DescriptorKey;
  areaId: string | null;
  itemId: string | null;
}

// Advisory only. A blank licence number on a non-Demo inspection warns (plan §10.1 / §16.3-C).
export type ReadinessWarning = 'licenceMissing';

export interface ReportReadiness {
  // Whether the New York readiness rules apply to this inspection at all (formal, supported
  // workflow). A Demo / internal-test inspection is not a New York client report.
  applies: boolean;
  ready: boolean;
  blockers: ReadinessBlocker[];
  warnings: ReadinessWarning[];
}

export interface ReportReadinessInput {
  inspection: V2Inspection;
  areas: readonly V2Area[];
  items: readonly V2ChecklistItem[];
  // The R1 eligibility answer: true only when the formal workflow gate is 'clear'.
  formal: boolean;
}

function observedRow(item: V2ChecklistItem): boolean {
  return item.status === 'checkedOk' || item.status === 'issueFound';
}

// An Unable row without a reason is not a recorded limitation yet: its descriptor still counts.
function unableWithoutReason(item: V2ChecklistItem): boolean {
  return item.status === 'unableToInspect' && item.reasonCode === null;
}

export function reportReadinessFor(input: ReportReadinessInput): ReportReadiness {
  const warnings: ReadinessWarning[] = [];
  const inspector = input.inspection.inspector;
  if (!input.inspection.agreement.demo && (inspector.licensedState !== 'NY' || inspector.licenseNumber.trim().length === 0)) warnings.push('licenceMissing');
  if (!input.formal) return { applies: false, ready: true, blockers: [], warnings };

  const blockers: ReadinessBlocker[] = [];
  for (const definition of DESCRIPTOR_CATALOGUE) {
    if (definition.scope !== 'property') continue;
    if (!propertyFactResolved(definition, propertyFactValue(input.inspection.property, definition.key))) {
      blockers.push({ kind: 'propertyFact', key: definition.key, areaId: null, itemId: null });
    }
  }

  const areas = input.areas.filter((a) => a.inspectionId === input.inspection.id && a.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  for (const area of areas) {
    const rows = input.items.filter((i) => i.areaId === area.id).sort((a, b) => a.sortOrder - b.sortOrder);
    for (const item of rows) {
      if (!observedRow(item) && !unableWithoutReason(item)) continue;
      for (const definition of descriptorsForTemplate(item.templateId)) {
        if (!descriptorResolved(definition, itemDescriptorAnswer(item, definition.key))) {
          blockers.push({ kind: 'descriptor', key: definition.key, areaId: area.id, itemId: item.id });
        }
      }
    }
  }
  return { applies: true, ready: blockers.length === 0, blockers, warnings };
}
